/**
 * ClientRequiredGate — renders children only when a client is selected.
 * When no client is selected it shows an empty state with CTAs.
 */

import React, { useState } from 'react';
import { PersonStanding, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SelectClientDialog } from '@/components/select-client-dialog';
import { useClient } from '@/context/client-context';

interface ClientRequiredGateProps {
  children: React.ReactNode;
}

const ClientRequiredGate: React.FC<ClientRequiredGateProps> = ({ children }) => {
  const { client } = useClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (client) return <>{children}</>;

  return (
    <div className="flex flex-col gap-8 py-4">
      <div className="flex items-start">
        <Card className="max-w-lg w-full">
          <CardHeader>
            <div className="bg-primary/10 text-primary p-3 rounded-full mb-4 w-fit">
              <PersonStanding className="h-8 w-8" />
            </div>
            <CardTitle>Select a Client to Begin</CardTitle>
            <CardDescription>
              To create a drill plan, select an existing client or create a new one. The planner
              requires a client so it can match drills to their posture analysis and assessment
              results.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3">
              <SelectClientDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <Button className="h-12" onClick={() => setIsDialogOpen(true)}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Select Existing Client
                </Button>
              </SelectClientDialog>
              <Button asChild variant="secondary" className="h-12">
                <Link to="/clients/new">Create New Client</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientRequiredGate;
